<template>
<div>
  <v-dialog v-model="waiverRequiredDialog" :width="dialogWidth">
    <v-card>
      <v-card-title class="text-h5 grey lighten-2">
        {{ $t("registerEvent.membership_waiver_information") }}
      </v-card-title>

      <v-card-text>
        <template v-if="onlyOtherAdultCase">
          <v-row>
            <v-col cols="12">
              <div class="mb-2">{{ otherAdultInfoMessage }}</div>
              <br />
              <v-btn
                v-if="firstOtherAdultWaiver && isAccountManagerContextForWaiver(firstOtherAdultWaiver)"
                color="primary"
                elevation="2"
                @click="goToProfile"
              >
                {{ $t("registerEvent.membership_waiver_update_profile") }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
        <template v-else>
          <v-row>
            <v-col cols="12" class="membership-waiver-card" v-for="waiver in membershipWaiversUser" :key="getWaiverKey(waiver)">
            <v-card>
              <v-card-title class="membership-waiver-card-title">
                {{ getWaiverCardTitle(waiver) }}
              </v-card-title>
              <v-card-text class="membership-waiver-card-text">
                <div v-if="canAssignAdditionalSignatory(waiver)">
                  <p v-if="isMinorWaiver(waiver)" class="mt-0 mb-2 body-2">
                    {{ waiver.organizationName || "" }} {{ $t("waivers.requires_both_parents_signature", { minorName: getMinorName(waiver) }) }}
                  </p>
                  <p class="font-weight-bold mt-0 mb-3">{{ $t("waivers.event_locked_until_signed") }}</p>
                  <p>{{ $t("waivers.x_of_y_signed", { signed: getSignedSignatures(waiver), required: getRequiredSignatures(waiver) }) }}</p>

                  <div class="mb-4">
                    <p class="subtitle-1 font-weight-bold mb-2">{{ $t("waivers.current_signatories") }}</p>
                    <div v-if="getAllSignatories(waiver).length > 0">
                      <v-chip
                        v-for="sig in getAllSignatories(waiver)"
                        :key="`${getWaiverKey(waiver)}-${sig.waiverInstanceId || sig.userId || sig.email}`"
                        class="ma-1"
                        :color="sig.waiverStatus === 'Signed' ? 'green' : '#b71c1c'"
                        text-color="white"
                      >
                        {{ sig.name }}
                        <span v-if="sig.isSelf" class="ml-1">{{ $t("waivers.you_label") }}</span>
                        <span class="ml-1 text-caption">({{ sig.waiverStatus === 'Signed' ? $t("waivers.waiversigned") : $t("waivers.pending") }})</span>
                      </v-chip>
                    </div>
                    <p v-else class="grey--text">{{ $t("waivers.no_signatories_assigned") }}</p>
                  </div>

                  <v-divider class="mb-4"></v-divider>

                  <p class="subtitle-1 font-weight-bold mb-2">{{ $t("waivers.add_signatory_section") }}</p>
                  <p>{{ $t("waivers.select_additional_signatory") }}</p>

                  <div v-if="!isAccountManagerContextForWaiver(waiver)">
                    <div v-if="(managerOptionsByWaiver[getWaiverKey(waiver)] || []).length > 0" class="d-flex flex-wrap">
                      <v-btn
                        v-for="manager in managerOptionsByWaiver[getWaiverKey(waiver)]"
                        :key="`${getWaiverKey(waiver)}-manager-${manager.userId}`"
                        color="primary"
                        elevation="2"
                        class="white--text ma-1"
                        :disabled="manager.isAlreadyAssigned === true || isSignatoryAlreadyAssigned(waiver, manager)"
                        :loading="loadingByWaiver[getWaiverKey(waiver)] === true"
                        @click="assignAdditionalSignatoryByEmail(waiver, manager.email)"
                      >
                        {{ manager.label }}
                      </v-btn>
                    </div>
                    <div v-else class="text-caption">
                      {{ $t("registerEvent.membership_waiver_no_account_managers_available") }}
                    </div>
                  </div>
                  <div v-else>
                    <div v-if="(relatedAdultOptionsByWaiver[getWaiverKey(waiver)] || []).length > 0" class="d-flex flex-wrap mb-2">
                      <v-btn
                        v-for="adult in relatedAdultOptionsByWaiver[getWaiverKey(waiver)]"
                        :key="`${getWaiverKey(waiver)}-adult-${adult.userId}`"
                        color="primary"
                        elevation="2"
                        class="white--text ma-1"
                        :disabled="adult.isAlreadyAssigned === true || isSignatoryAlreadyAssigned(waiver, adult)"
                        :loading="loadingByWaiver[getWaiverKey(waiver)] === true"
                        @click="assignAdditionalSignatoryByEmail(waiver, adult.email)"
                      >
                        {{ adult.label }}
                      </v-btn>
                    </div>
                    <div class="mt-4">
                      <p>{{ $t("waivers.or_enter_email") }}</p>
                      <v-text-field
                        v-model="emailByWaiver[getWaiverKey(waiver)]"
                        :label="$t('waivers.signatory_email')"
                        :error-messages="emailErrorByWaiver[getWaiverKey(waiver)] ? [$t('registerEvent.membership_waiver_invalid_email')] : []"
                        :disabled="loadingByWaiver[getWaiverKey(waiver)] === true"
                        type="email"
                        outlined
                        dense
                        hide-details="auto"
                      ></v-text-field>
                      <SignatoryAccountStepper
                        v-if="showNoAccountFormByWaiver[getWaiverKey(waiver)]"
                        :signatory-email="(emailByWaiver[getWaiverKey(waiver)] || '').trim()"
                        :intro-text="$t('waivers.user_not_found_collect_info')"
                        :min-adult-age="Number(waiver.adultAge || 18)"
                        @created="handleCreatedNoAccountSignatory(waiver, $event)"
                        @cancel="closeNoAccountRegistration(waiver)"
                      />
                      <v-btn
                        v-else
                        color="primary"
                        elevation="2"
                        class="white--text mt-2"
                        :loading="loadingByWaiver[getWaiverKey(waiver)] === true"
                        :disabled="loadingByWaiver[getWaiverKey(waiver)] === true || !(emailByWaiver[getWaiverKey(waiver)] || '').trim()"
                        @click="assignAdditionalSignatoryByEmail(waiver, (emailByWaiver[getWaiverKey(waiver)] || '').trim())"
                      >
                        {{ $t("waivers.send_waiver_email") }}
                      </v-btn>
                    </div>
                  </div>

                  <v-divider class="my-4"></v-divider>

                  <p v-if="isMinorWaiver(waiver)" class="body-2">
                    {{ $t("waivers.sole_guardian_explanation", { minorName: getMinorName(waiver) }) }}
                  </p>
                  <v-btn
                    color="warning"
                    outlined
                    small
                    block
                    class="sole-guardian-btn"
                    :loading="loadingByWaiver[getWaiverKey(waiver)] === true"
                    :disabled="loadingByWaiver[getWaiverKey(waiver)] === true"
                    @click="certifySoleGuardian(waiver)"
                  >
                    <v-icon left small>mdi-shield-check</v-icon>
                    {{ $t("registerEvent.certify_sole_guardian") }}
                  </v-btn>
                </div>
                <div
                  v-if="!isWaiverSigned(waiver) && hasAssignedSignatory(waiver) && !isAssignedToCurrentUser(waiver) && !isWaiverRejected(waiver)"
                >
                  {{ $t("registerEvent.membership_waiver_assigned_message", {
                    signatoryName: getSignatoryName(waiver),
                    minorName: getMinorName(waiver)
                  }) }}
                </div>
                <div v-else-if="!isWaiverSigned(waiver) && isMinorWaiver(waiver)">
                  <p class="mb-2">{{ $t("registerEvent.membership_waiver_guardian_question") }}</p>

                  <v-radio-group
                    v-model="guardianAnswer[getWaiverKey(waiver)]"
                    @change="onGuardianAnswerChange(waiver)"
                    row
                    :disabled="loadingByWaiver[getWaiverKey(waiver)] === true"
                    hide-details
                    class="mt-0"
                  >
                    <v-radio :label="$t('common.yes')" value="yes"></v-radio>
                    <v-radio :label="$t('common.no')" value="no"></v-radio>
                  </v-radio-group>

                  <div v-if="guardianAnswer[getWaiverKey(waiver)] === 'yes'" class="mt-2">
                    <v-btn
                      color="primary"
                      elevation="2"
                      :loading="loadingByWaiver[getWaiverKey(waiver)] === true"
                      @click="signAsCurrentUser(waiver)"
                    >
                      {{ $t("registerEvent.sign_now") }}
                    </v-btn>
                  </div>

                  <div v-if="guardianAnswer[getWaiverKey(waiver)] === 'no'" class="mt-2">
                    <p class="mb-2">{{ waiver.isMembershipWaiver ? $t("registerEvent.membership_waiver_select_account_manager") : $t("registerEvent.membership_waiver_email_helper") }}</p>
                    <v-select
                      v-if="waiver.isMembershipWaiver && !isAccountManagerContextForWaiver(waiver)"
                      v-model="selectedManagerByWaiver[getWaiverKey(waiver)]"
                      :items="managerOptionsByWaiver[getWaiverKey(waiver)] || []"
                      item-text="label"
                      item-value="email"
                      :error-messages="managerErrorByWaiver[getWaiverKey(waiver)] ? [$t('registerEvent.account_manager_signatory_required')] : []"
                      :hint="$t('registerEvent.membership_waiver_no_search_account_manager_only')"
                      persistent-hint
                      :disabled="loadingByWaiver[getWaiverKey(waiver)] === true"
                      outlined
                      dense
                      hide-details="auto"
                    ></v-select>
                    <v-text-field
                      v-else
                      v-model="emailByWaiver[getWaiverKey(waiver)]"
                      :label="$t('registerEvent.membership_waiver_signatory_email')"
                      :error-messages="emailErrorByWaiver[getWaiverKey(waiver)] ? [$t('registerEvent.membership_waiver_invalid_email')] : []"
                      :disabled="loadingByWaiver[getWaiverKey(waiver)] === true"
                      outlined
                      dense
                      hide-details="auto"
                    ></v-text-field>
                    <SignatoryAccountStepper
                      v-if="showNoAccountFormByWaiver[getWaiverKey(waiver)]"
                      :signatory-email="(emailByWaiver[getWaiverKey(waiver)] || '').trim()"
                      :intro-text="$t('waivers.user_not_found_collect_info')"
                      :min-adult-age="Number(waiver.adultAge || 18)"
                      @created="handleCreatedNoAccountSignatory(waiver, $event)"
                      @cancel="closeNoAccountRegistration(waiver)"
                    />
                    <v-btn
                      v-else
                      color="primary"
                      elevation="2"
                      class="mt-2"
                      :loading="loadingByWaiver[getWaiverKey(waiver)] === true"
                      :disabled="waiver.isMembershipWaiver && !isAccountManagerContextForWaiver(waiver) && !(selectedManagerByWaiver[getWaiverKey(waiver)] || '').trim()"
                      @click="findOrCreateSignatory(waiver)"
                    >
                      {{ $t("registerEvent.membership_waiver_find_or_create_signatory") }}
                    </v-btn>
                  </div>
                </div>

                <div v-else-if="!isWaiverSigned(waiver) && canSignAdultWaiver(waiver)">
                  <v-btn
                    color="primary"
                    elevation="2"
                    :disabled="loadingByWaiver[getWaiverKey(waiver)] === true"
                    :loading="loadingByWaiver[getWaiverKey(waiver)] === true"
                    @click="signOrSendWaiver(waiver)"
                  >
                    {{ $t("registerEvent.sign_now") }}
                  </v-btn>
                </div>
                <div v-else-if="isOtherAdultWaiver(waiver)" class="mt-2 text-caption">
                  {{ $t("registerEvent.membership_waiver_adult_card_hint") }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
          <v-row v-if="hasOtherAdultUnsignedWaivers" class="mt-4">
            <v-col cols="12">
              <div class="mb-2">{{ otherAdultInfoMessage }}</div>
              <v-btn
                v-if="firstOtherAdultWaiver && isAccountManagerContextForWaiver(firstOtherAdultWaiver)"
                color="primary"
                elevation="2"
                @click="goToProfile"
              >
                {{ $t("registerEvent.membership_waiver_update_profile") }}
              </v-btn>
            </v-col>
          </v-row>
        </template>
      </v-card-text>

      <v-divider></v-divider>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="primary" text @click="waiverRequiredDialog = false">
          {{ $t("common.close") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  <v-dialog v-model="confirmSoleGuardianModal" max-width="500">
    <v-card>
      <v-card-title class="headline" style="word-break: break-word;">{{ $t('registerEvent.certify_sole_guardian') }}</v-card-title>
      <v-card-text>
        {{ $t("registerEvent.confirm_sole_guardian") }}
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn color="grey" text @click="confirmSoleGuardianModal = false; waiverToCertify = null">
          {{ $t('common.cancel') }}
        </v-btn>
        <v-btn color="primary" text @click="executeCertifySoleGuardian">
          {{ $t('common.ok') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</div>
</template>

<script>
import { mapState } from "vuex";
import {
  VDialog,
  VCard,
  VCardTitle,
  VCardText,
  VRow,
  VCol,
  VDivider,
  VCardActions,
  VSpacer,
  VBtn,
  VRadioGroup,
  VRadio,
  VTextField,
  VSelect,
  VChip,
  VIcon
} from "vuetify/lib";
import EventWaiverService from "@/services/EventWaiverService";
import RelationshipService from "@/services/RelationshipService";
import WaiverService from "@/services/WaiverService";
import AuthService from "@/services/AuthService";
import MembershipWaiverService from "@/services/MembershipWaiverService";
import SignatoryAccountStepper from "@/components/waiver/SignatoryAccountStepper.vue";

export default {
  name: "MembershipWaiverDialog",
  components: {
    VDialog,
    VCard,
    VCardTitle,
    VCardText,
    VRow,
    VCol,
    VDivider,
    VCardActions,
    VSpacer,
    VBtn,
    VRadioGroup,
    VRadio,
    VTextField,
    VSelect,
    VChip,
    VIcon,
    SignatoryAccountStepper
  },
  props: {
    role: { type: String, default: "Spectator" },
    waiverDialog: { type: Boolean, default: false },
    bSend: { type: Boolean, default: false },
    selectedParticipant: { type: Object, default: null },
    selectedParticipantIsMinor: { type: Boolean, default: null },
    isAccountManagerForParticipant: { type: Boolean, default: false },
    componentFather: { type: String, default: "" }
  },
  data() {
    return {
      guardianAnswer: {},
      emailByWaiver: {},
      emailErrorByWaiver: {},
      loadingByWaiver: {},
      assignedByWaiver: {},
      managerOptionsByWaiver: {},
      relatedAdultOptionsByWaiver: {},
      selectedManagerByWaiver: {},
      managerErrorByWaiver: {},
      showNoAccountFormByWaiver: {},
      noAccountActionByWaiver: {},
      confirmSoleGuardianModal: false,
      waiverToCertify: null,
      soleGuardianWaivers: {}
    };
  },
  computed: {
    ...mapState({
      waiversUser: (state) => state.waiverDialog.waiversUser,
      userStore: (state) => state.session.user
    }),
    waiverRequiredDialog: {
      get() {
        return this.waiverDialog;
      },
      set(value) {
        this.$emit("changeField", { field: "waiverRequiredDialog", value });
      }
    },
    bSendWaiver: {
      get() {
        return this.bSend;
      },
      set(value) {
        this.$emit("changeField", { field: "bSendWaiver", value });
      }
    },
    membershipWaiversUser() {
      const rows = Array.isArray(this.waiversUser)
        ? this.waiversUser.filter((waiver) => {
            if (!waiver) return false;
            if (waiver.sourceColumn) return waiver.sourceColumn === 'membershipWaiver';
            return waiver.isMembershipWaiver === true;
          })
        : [];
      return rows.sort((left, right) => {
        const leftPending = this.isWaiverSigned(left) ? 1 : 0;
        const rightPending = this.isWaiverSigned(right) ? 1 : 0;
        if (leftPending !== rightPending) {
          return leftPending - rightPending;
        }
        const leftName = `${left?.riderFirstName || ""} ${left?.riderLastName || ""}`.trim().toLowerCase();
        const rightName = `${right?.riderFirstName || ""} ${right?.riderLastName || ""}`.trim().toLowerCase();
        return leftName.localeCompare(rightName);
      });
    },
    hasOtherAdultUnsignedWaivers() {
      return this.membershipWaiversUser.some((w) => this.isOtherAdultWaiver(w));
    },
    onlyOtherAdultCase() {
      const allOtherAdult = this.membershipWaiversUser.length > 0 && this.membershipWaiversUser.every((w) => this.isOtherAdultWaiver(w));
      return allOtherAdult;
    },
    firstOtherAdultWaiver() {
      return this.membershipWaiversUser.find((w) => this.isOtherAdultWaiver(w)) || null;
    },
    hasNoAccountStepperOpen() {
      return Object.values(this.showNoAccountFormByWaiver || {}).some((value) => value === true);
    },
    hasMultiSignatureWaiver() {
      return this.membershipWaiversUser.some((waiver) => this.canAssignAdditionalSignatory(waiver));
    },
    dialogWidth() {
      if (this.hasNoAccountStepperOpen) {
        return 950;
      }
      if (this.hasMultiSignatureWaiver) {
        return 850;
      }
      return 500;
    },
    otherAdultInfoMessage() {
      return this.getOtherAdultInfoMessage(this.firstOtherAdultWaiver);
    },
    isAccountManagerContext() {
      return this.firstOtherAdultWaiver ? this.isAccountManagerContextForWaiver(this.firstOtherAdultWaiver) : false;
    }
  },
  methods: {
    isAccountManagerContextForWaiver(waiver) {
      const currentUserId = Number(this.userStore?.id || 0);
      if (!currentUserId) return false;
      const riderUserId = Number(waiver?.riderUserId || waiver?.userId || 0);
      const selectedUserId = Number(this.selectedParticipant?.userId || 0);
      if (this.isAccountManagerForParticipant === true && selectedUserId > 0 && riderUserId === selectedUserId) {
        return true;
      }
      const accountManagerUserId = Number(
        waiver?.accountManagerUserId ??
        waiver?.fkUsersIdMaster ??
        (riderUserId === selectedUserId ? (this.selectedParticipant?.accountManagerUserId ?? this.selectedParticipant?.fkUsersIdMaster) : 0) ??
        0
      );
      if (accountManagerUserId > 0 && accountManagerUserId === currentUserId) {
        return true;
      }
      const managerAccountRaw = (
        waiver?.managerAccount ??
        (riderUserId === selectedUserId ? this.selectedParticipant?.managerAccount : "") ??
        ""
      ).toString().trim();
      if (!managerAccountRaw) {
        return false;
      }
      if (managerAccountRaw.toLowerCase() === "you") {
        return true;
      }
      const managerIds = managerAccountRaw
        .split(",")
        .map((s) => parseInt(String(s).trim(), 10))
        .filter((n) => Number.isInteger(n) && n > 0);
      return managerIds.includes(currentUserId);
    },
    isOtherAdultWaiver(waiver) {
      if (!waiver) return false;
      if (this.isWaiverSigned(waiver)) return false;
      if (this.hasAssignedSignatory(waiver)) return false;
      if (this.isMinorWaiver(waiver)) return false;
      if (this.canSignAdultWaiver(waiver)) return false;
      return true;
    },
    isWaiverSigned(waiver) {
      const statusRaw = (waiver?.waiverstatus || waiver?.waiverStatus || waiver?.status || "")
        .toString()
        .trim()
        .toLowerCase();
      return statusRaw === "signed" || statusRaw === "2" || statusRaw === "4";
    },
    isWaiverRejected(waiver) {
      const statusRaw = (waiver?.waiverstatus || waiver?.waiverStatus || waiver?.status || "")
        .toString()
        .trim()
        .toLowerCase();
      return statusRaw === "rejected" || statusRaw === "3";
    },
    getRequiredSignatures(waiver) {
      return Number(waiver?.signaturesRequired || waiver?.qtSignReq || waiver?.required || 0);
    },
    getSignedSignatures(waiver) {
      return Number(waiver?.signaturesSigned || 0);
    },
    isAllSignaturesComplete(waiver) {
      if (typeof waiver?.allSignaturesComplete === "boolean") {
        return waiver.allSignaturesComplete;
      }
      const required = this.getRequiredSignatures(waiver);
      if (required <= 1) {
        return this.isWaiverSigned(waiver);
      }
      return this.getSignedSignatures(waiver) >= required;
    },
    hasCurrentUserSigned(waiver) {
      const currentId = Number(this.userStore?.id || 0);
      if (!currentId) return false;
      const signatories = Array.isArray(waiver?.signatories) ? waiver.signatories : [];
      const fromSignatories = signatories.some((sig) => {
        const sigId = Number(sig?.userId || 0);
        const statusRaw = (sig?.waiverStatus || sig?.status || "").toString().trim().toLowerCase();
        const isSigned = statusRaw === "signed" || statusRaw === "2" || statusRaw === "4";
        return sigId === currentId && isSigned;
      });
      if (fromSignatories) return true;
      const signUserId = Number(waiver?.signUserId || 0);
      return signUserId === currentId && this.isWaiverSigned(waiver);
    },
    canAssignAdditionalSignatory(waiver) {
      if (!waiver?.isMembershipWaiver) return false;
      if (!this.isMinorWaiver(waiver)) return false;
      if (this.isAllSignaturesComplete(waiver)) return false;
      const required = this.getRequiredSignatures(waiver);
      if (required <= 1) return false;
      if (this.hasCurrentUserSigned(waiver)) return true;
      return this.getSignedSignatures(waiver) > 0;
    },
    resolveLivelapsHost() {
      const fromStore = this.$store?.getters?.env?.get("VUE_APP_LIVELAPS_HOST");
      const fromProcess = process.env.VUE_APP_LIVELAPS_HOST;
      const normalized = (fromStore || fromProcess || "").toString().trim().replace(/\/+$/, "");
      if (normalized) {
        return normalized;
      }
      return `${window.location.origin}/livelaps`;
    },
    buildWaiverUrlFromBackendLink(link) {
      const host = this.resolveLivelapsHost();
      if (/^https?:\/\//i.test(link)) {
        const parsed = new URL(link);
        return `${host}${parsed.pathname}${parsed.search}${parsed.hash}`;
      }
      if (link.startsWith("/")) {
        return `${host}${link}`;
      }
      return `${host}/${link}`;
    },
    openSignedWaiverLink(waiver) {
      const link = (waiver?.waiverLink || waiver?.link || "").toString().trim();
      if (!link) {
        this.$emit("alertNotValidate", this.$t("registerEvent.membership_waiver_link_unavailable"));
        return;
      }
      let finalUrl = link;
      try {
        // Keep route from backend link and force base host from app env
        // (which can come from .env or Strapi-loaded parameters).
        finalUrl = this.buildWaiverUrlFromBackendLink(link);
      } catch (error) {
        finalUrl = link;
      }
      window.open(finalUrl, "_blank", "noopener,noreferrer");
    },
    getWaiverTrace(waiver) {
      return {
        traceId: waiver?._traceId || "no-trace",
        userId: waiver?.userId,
        racerUserId: waiver?.racerUserId,
        membershipWaiverTemplateId: waiver?.membershipWaiverTemplateId,
        eventWaiverId: waiver?.eventWaiverId,
        fkWaiverId: waiver?.fkWaiverId,
        waiverId: waiver?.id,
        title: waiver?.waiverTranslate?.first?.title
      };
    },
    getWaiverKey(waiver) {
      if (waiver && waiver.dialogKey) {
        return waiver.dialogKey;
      }
      const userId = waiver.userId || "user";
      const templateId = waiver.membershipWaiverTemplateId || waiver.templateWaiverId || "";
      if (templateId) {
        return `${userId}-${templateId}`;
      }
      const eventWaiverId = waiver.eventWaiverId || "eventWaiver";
      const waiverId = waiver.id || "waiverId";
      const fkWaiverId = waiver.fkWaiverId || "fkWaiverId";
      const title = waiver.nameWaiver || waiver.displayName || waiver.waiverName || waiver.waiverTranslate?.first?.title || "waiver";
      return `${eventWaiverId}-${waiverId}-${fkWaiverId}-${userId}-${title}`;
    },
    openNoAccountRegistration(waiver, action = "primary", signatoryEmail = "") {
      const key = this.getWaiverKey(waiver);
      if (signatoryEmail) {
        this.emailByWaiver = { ...this.emailByWaiver, [key]: signatoryEmail };
      }
      this.showNoAccountFormByWaiver = { ...this.showNoAccountFormByWaiver, [key]: true };
      this.noAccountActionByWaiver = { ...this.noAccountActionByWaiver, [key]: action };
    },
    translateMembershipWaiverError(waiver, errKey) {
      if (errKey === "signatory_must_be_adult") {
        return this.$t("waivers.signatory_must_be_adult", { age: Number(waiver?.adultAge || 18) });
      }

      if (this.$te(`registerEvent.${errKey}`)) {
        return this.$t(`registerEvent.${errKey}`);
      }

      if (this.$te(`waivers.${errKey}`)) {
        return this.$t(`waivers.${errKey}`);
      }

      return this.$t("registerEvent.membership_waiver_assignment_failed");
    },
    closeNoAccountRegistration(waiver) {
      const key = this.getWaiverKey(waiver);
      this.showNoAccountFormByWaiver = { ...this.showNoAccountFormByWaiver, [key]: false };
      this.noAccountActionByWaiver = { ...this.noAccountActionByWaiver, [key]: null };
    },
    async handleCreatedNoAccountSignatory(waiver, payload) {
      const key = this.getWaiverKey(waiver);
      const signatoryEmail = (payload?.email || this.emailByWaiver[key] || "").trim();
      this.emailByWaiver = { ...this.emailByWaiver, [key]: signatoryEmail };
      const action = this.noAccountActionByWaiver[key] || "primary";
      this.closeNoAccountRegistration(waiver);
      if (action === "additional") {
        await this.assignAdditionalSignatoryByEmail(waiver, signatoryEmail);
        return;
      }
      await this.findOrCreateSignatory(waiver);
    },
    isMinorWaiver(waiver) {
      if (waiver && waiver.isMinor !== undefined && waiver.isMinor !== null && `${waiver.isMinor}` !== "") {
        return Number(waiver.isMinor) === 1;
      }
      if (waiver && waiver.age18 !== undefined && waiver.age18 !== null) {
        return waiver.age18 === false;
      }
      if (this.selectedParticipantIsMinor !== undefined && this.selectedParticipantIsMinor !== null) {
        return this.selectedParticipantIsMinor === true;
      }
      return false;
    },
    canSignAdultWaiver(waiver) {
      const currentId = Number(this.userStore?.id || 0);
      if (!currentId) return false;
      const riderId = Number(waiver?.userId || waiver?.racerUserId || waiver?.riderUserId || 0);
      if (riderId === currentId) return true;
      const selectedId = Number(this.selectedParticipant?.userId || 0);
      return selectedId === currentId && (riderId === selectedId || riderId === 0);
    },
    onGuardianAnswerChange(waiver) {
      const key = this.getWaiverKey(waiver);
      this.emailErrorByWaiver = { ...this.emailErrorByWaiver, [key]: false };
      this.managerErrorByWaiver = { ...this.managerErrorByWaiver, [key]: false };
      if (this.guardianAnswer[key] === "no" && waiver?.isMembershipWaiver) {
        this.loadAccountManagerOptionsForWaiver(waiver);
        if (this.isAccountManagerContextForWaiver(waiver)) {
          this.loadRelatedAdultOptionsForWaiver(waiver);
        }
      }
    },
    async loadAccountManagerOptionsForWaiver(waiver) {
      const key = this.getWaiverKey(waiver);
      const riderUserId = Number(waiver.racerUserId || waiver.userId || 0);
      const membershipWaiverTemplateId = this.getMembershipWaiverId(waiver);
      if (!riderUserId || !membershipWaiverTemplateId) {
        this.managerOptionsByWaiver = { ...this.managerOptionsByWaiver, [key]: [] };
        return;
      }
      try {
        const response = await MembershipWaiverService.getEligibleSignatories({
          riderUserId,
          membershipWaiverTemplateId,
          onlyAccountManagers: true,
          includeAlreadyAssigned: true
        });
        const rows = Array.isArray(response?.data?.message) ? response.data.message : [];
        const options = rows
          .filter((row) => this.isValidEmail(row?.email))
          .map((row) => ({
            userId: Number(row.userId || 0),
            email: (row.email || "").trim(),
            label: `${(row.firstName || "").trim()} ${(row.lastName || "").trim()}`.trim() || (row.email || "").trim(),
            isAlreadyAssigned: row?.isAlreadyAssigned === true
          }));
        this.managerOptionsByWaiver = { ...this.managerOptionsByWaiver, [key]: options };
      } catch (error) {
        this.managerOptionsByWaiver = { ...this.managerOptionsByWaiver, [key]: [] };
      }
    },
    async loadRelatedAdultOptionsForWaiver(waiver) {
      const key = this.getWaiverKey(waiver);
      const riderUserId = Number(waiver.racerUserId || waiver.userId || 0);
      const membershipWaiverTemplateId = this.getMembershipWaiverId(waiver);
      if (!riderUserId || !membershipWaiverTemplateId) {
        this.relatedAdultOptionsByWaiver = { ...this.relatedAdultOptionsByWaiver, [key]: [] };
        return;
      }
      try {
        const response = await MembershipWaiverService.getEligibleSignatories({
          riderUserId,
          membershipWaiverTemplateId,
          includeAlreadyAssigned: true
        });
        const rows = Array.isArray(response?.data?.message) ? response.data.message : [];
        const options = rows
          .filter((row) => this.isValidEmail(row?.email))
          .map((row) => ({
            userId: Number(row.userId || 0),
            email: (row.email || "").trim(),
            label: `${(row.firstName || "").trim()} ${(row.lastName || "").trim()}`.trim() || (row.email || "").trim(),
            isAlreadyAssigned: row?.isAlreadyAssigned === true
          }));
        this.relatedAdultOptionsByWaiver = { ...this.relatedAdultOptionsByWaiver, [key]: options };
      } catch (error) {
        this.relatedAdultOptionsByWaiver = { ...this.relatedAdultOptionsByWaiver, [key]: [] };
      }
    },
    isSignatoryAlreadyAssigned(/* waiver, option */) {
      // Allow duplicate signatures for single parents (issue #46)
      return false;
    },
    getMinorName(waiver) {
      return `${waiver.riderFirstName || ""} ${waiver.riderLastName || ""}`.trim() ||
        waiver.fullName ||
        `${waiver.participantName || ""} ${waiver.participantLastname || ""}`.trim() ||
        "";
    },
    getWaiverTitle(waiver) {
      return (waiver.waiverTranslate && waiver.waiverTranslate[this.$i18n.locale] && waiver.waiverTranslate[this.$i18n.locale].title) ||
        (waiver.waiverTranslate && waiver.waiverTranslate.first && waiver.waiverTranslate.first.title) ||
        waiver.membershipWaiverTitle ||
        waiver.waiverTitle ||
        "";
    },
    getWaiverCardTitle(waiver) {
      const title = this.getWaiverTitle(waiver);
      const minorName = this.getMinorName(waiver);
      if (!minorName) {
        return title;
      }
      if (title.toLowerCase().indexOf(minorName.toLowerCase()) !== -1) {
        return title;
      }
      return `${title} ${minorName}`.trim();
    },
    getSignatoryName(waiver) {
      const key = this.getWaiverKey(waiver);
      if (this.assignedByWaiver[key] && this.assignedByWaiver[key].name) {
        return this.assignedByWaiver[key].name;
      }
      const fromWaiver = `${waiver.signatoryName || ""} ${waiver.signatoryLastname || ""}`.trim();
      return fromWaiver || waiver.signatoryEmail || "";
    },
    getAllSignatories(waiver) {
      const currentUserId = Number(this.userStore?.id || 0);
      const currentUserEmail = String(this.userStore?.emailAddress || this.userStore?.email || '').toLowerCase().trim();
      const active = Array.isArray(waiver?.signatories)
        ? waiver.signatories.filter((signatory) => signatory && (signatory.waiverStatus || '') !== 'Rejected')
        : [];
      const signatories = [];
      const seen = new Set();
      active.forEach((signatory) => {
        const name = String(signatory.name || `${signatory.firstName || ''} ${signatory.lastName || ''}`.trim() || signatory.email || '').trim();
        const email = String(signatory.email || '').toLowerCase().trim();
        const userId = Number(signatory.userId || 0);
        const key = userId > 0 ? `u:${userId}` : `e:${email}`;
        if (!name || seen.has(key)) {
          return;
        }
        seen.add(key);
        const isSelf = (currentUserId > 0 && userId === currentUserId) || (!!currentUserEmail && email === currentUserEmail);
        signatories.push({
          ...signatory,
          name,
          email,
          userId,
          isSelf,
          waiverStatus: signatory.waiverStatus || signatory.status || 'Pending'
        });
      });
      if (signatories.length === 0 && this.hasCurrentUserSigned(waiver)) {
        const name = `${this.userStore?.firstName || ''} ${this.userStore?.lastName || ''}`.trim() || this.getSignatoryName(waiver);
        signatories.push({
          _virtual: true,
          userId: currentUserId,
          email: currentUserEmail,
          name,
          isSelf: true,
          waiverStatus: 'Signed'
        });
      }
      return signatories;
    },
    getOtherAdultInfoMessage(waiver) {
      const userFullName = this.getOtherAdultFullName(waiver);
      const userEmail = this.getOtherAdultEmail(waiver);
      const key = this.isAccountManagerContextForWaiver(waiver)
        ? "registerEvent.membership_waiver_adult_message_account_manager"
        : "registerEvent.membership_waiver_adult_message";
      return this.$t(key, { userFullName, userEmail });
    },
    getOtherAdultFullName(waiver) {
      const fromWaiver = `${waiver?.riderFirstName || ""} ${waiver?.riderLastName || ""}`.trim() || waiver?.fullName || "";
      if (fromWaiver) return fromWaiver;
      const p = this.selectedParticipant;
      if (!p) return "";
      return `${p.fName || p.firstName || ""} ${p.lName || p.lastName || ""}`.trim() || p.name || "";
    },
    getOtherAdultEmail(waiver) {
      const fromWaiver = (waiver?.riderEmail || waiver?.email || "").toString().trim();
      if (fromWaiver) return fromWaiver;
      const p = this.selectedParticipant;
      const email = (p?.email || p?.emailAddress || "").toString().trim();
      if (email) return email;
      return this.$t("registerEvent.membership_waiver_adult_email_placeholder");
    },
    goToProfile() {
      this.waiverRequiredDialog = false;
      this.$router.push({ name: "Profile" }).catch(() => {});
    },
    getNameParts(fullName) {
      const value = (fullName || "").trim();
      if (!value) {
        return { firstName: "", lastName: "" };
      }
      const chunks = value.split(" ");
      const firstName = chunks.shift() || "";
      const lastName = chunks.join(" ");
      return { firstName, lastName };
    },
    hasAssignedSignatory(waiver) {
      if (this.isWaiverRejected(waiver)) {
        // Rejected instances must be re-signable.
        return false;
      }
      const key = this.getWaiverKey(waiver);
      if (this.assignedByWaiver[key]) {
        return true;
      }
      const signUserId = Number(waiver.signUserId || 0);
      const riderUserId = Number(waiver.racerUserId || waiver.userId || 0);
      if (signUserId > 0 && signUserId !== riderUserId) {
        return true;
      }
      const signatoryFullName = `${waiver.signatoryName || ""} ${waiver.signatoryLastname || ""}`.trim();
      if (!signatoryFullName) {
        return false;
      }
      const normalize = (value) => (value || "").toString().trim().toLowerCase().replace(/\s+/g, " ");
      const riderFullName = `${waiver.riderFirstName || waiver.participantName || ""} ${waiver.riderLastName || waiver.participantLastname || ""}`.trim();
      return normalize(signatoryFullName) !== normalize(riderFullName);
    },
    isAssignedToCurrentUser(waiver) {
      const signUserId = Number(waiver.signUserId || 0);
      const currentUserId = Number(this.userStore?.id || 0);
      if (signUserId > 0 && currentUserId > 0 && signUserId === currentUserId) {
        return true;
      }
      const currentEmail = (this.userStore?.emailAddress || this.userStore?.email || "").toString().trim().toLowerCase();
      const signatoryEmail = (waiver?.signatoryEmail || "").toString().trim().toLowerCase();
      return !!currentEmail && !!signatoryEmail && currentEmail === signatoryEmail;
    },
    isValidEmail(email) {
      const value = (email || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
    },
    getMembershipWaiverId(waiver) {
      return Number(
        waiver.membershipWaiverTemplateId ||
        waiver.fkWaiverId ||
        waiver.waiverInstanceId ||
        waiver.eventWaiverId ||
        waiver.id ||
        0
      );
    },
    buildMembershipGeneratePayload(waiver, extra = {}) {
      const membershipWaiverId = this.getMembershipWaiverId(waiver);
      const riderUserId = Number(waiver.racerUserId || waiver.userId || 0);
      const payload = {
        membershipWaiverId,
        userId: riderUserId,
        eventId: Number(waiver.eventId || 0) || undefined,
        organizationId: waiver.organizationId || null,
        requestedByUserId: Number(this.userStore?.id || 0),
        legalGuardianAnswer: extra.legalGuardianAnswer ?? null,
        signatoryEmail: extra.signatoryEmail || null
      };
      return payload;
    },
    normalizeMembershipGenerateMessage(message, waiver) {
      if (!message || typeof message !== "object") {
        return message;
      }
      return {
        ...message,
        fkUserId: message.fkUserId || waiver.racerUserId || waiver.userId,
        userId: message.userId || waiver.racerUserId || waiver.userId
      };
    },
    async executeMembershipGenerate(waiver, extra = {}) {
      const payload = this.buildMembershipGeneratePayload(waiver, extra);
      if (!payload.membershipWaiverId || !payload.userId) {
        throw new Error("membership_waiver_generation_failed");
      }
      const response = await MembershipWaiverService.generateByMembershipWaiverId(payload);
      if (response?.data?.success !== 1) {
        throw new Error(response?.data?.message || "membership_waiver_generation_failed");
      }
      const message = response?.data?.message || {};
      const action = (message.action || "").toString().toLowerCase();
      if (action === "email_sent") {
        this.$emit("showAlert", { msg: this.$t("registerEvent.send_email_successfully"), color: "success" });
        this.$emit("refreshMembershipWaiverStatus");
        return;
      }
      const normalizedMessage = this.normalizeMembershipGenerateMessage(message, waiver);
      if (normalizedMessage?.id) {
        this.$emit("addMembershipWaiver", normalizedMessage);
        this.$emit("refreshMembershipWaiverStatus");
        return;
      }
      throw new Error("membership_waiver_generation_failed");
    },
    async signAsCurrentUser(waiver) {
      const key = this.getWaiverKey(waiver);
      this.loadingByWaiver = { ...this.loadingByWaiver, [key]: true };
      try {
        if (waiver.isMembershipWaiver) {
          await this.executeMembershipGenerate(waiver, { legalGuardianAnswer: "yes" });
          if (this.soleGuardianWaivers[key]) {
            await this.createSoleGuardianCopy(waiver);
          }
          return;
        }
        await this.assignSignatoryToWaiver(waiver, this.userStore.id);
        const userFirstName = this.userStore?.firstName || "";
        const userLastName = this.userStore?.lastName || "";
        this.assignedByWaiver = {
          ...this.assignedByWaiver,
          [key]: { name: `${userFirstName} ${userLastName}`.trim() }
        };
        waiver.signUserId = this.userStore.id;
        waiver.signatoryName = userFirstName || waiver.signatoryName || "";
        waiver.signatoryLastname = userLastName || waiver.signatoryLastname || "";
        await this.signOrSendWaiver(waiver, false);
        if (this.soleGuardianWaivers[key]) {
          await this.createSoleGuardianCopy(waiver);
        }
      } catch (error) {
        const keyFromError = (error?.message || "").toString().trim();
        this.$emit("alertNotValidate", this.translateMembershipWaiverError(waiver, keyFromError));
      } finally {
        this.loadingByWaiver = { ...this.loadingByWaiver, [key]: false };
      }
    },
    certifySoleGuardian(waiver) {
      this.waiverToCertify = waiver;
      this.confirmSoleGuardianModal = true;
    },
    async executeCertifySoleGuardian() {
      const waiver = this.waiverToCertify;
      if (!waiver) return;
      this.confirmSoleGuardianModal = false;
      this.waiverToCertify = null;

      const key = this.getWaiverKey(waiver);
      this.$set(this.soleGuardianWaivers, key, true);

      if (this.hasCurrentUserSigned(waiver)) {
        this.loadingByWaiver = { ...this.loadingByWaiver, [key]: true };
        await this.createSoleGuardianCopy(waiver);
        this.loadingByWaiver = { ...this.loadingByWaiver, [key]: false };
        this.$emit("refreshMembershipWaiverStatus");
      }
    },
    async createSoleGuardianCopy(waiver) {
      const signatoryEmail = (this.userStore?.emailAddress || this.userStore?.email || "").trim();
      if (!signatoryEmail) return;
      try {
        await MembershipWaiverService.addAnotherSignatory({
          membershipWaiverTemplateId: Number(waiver.membershipWaiverTemplateId || waiver.fkWaiverId || 0),
          riderUserId: Number(waiver.racerUserId || waiver.userId || 0),
          signatoryEmail,
          isSoleGuardian: true
        });
      } catch (e) {
        // Copy creation failed silently — primary signature is already saved.
      }
    },
    async assignAdditionalSignatoryByEmail(waiver, rawEmail) {
      const key = this.getWaiverKey(waiver);
      const signatoryEmail = (rawEmail || "").trim();
      this.emailErrorByWaiver = { ...this.emailErrorByWaiver, [key]: false };
      this.managerErrorByWaiver = { ...this.managerErrorByWaiver, [key]: false };
      if (!this.isValidEmail(signatoryEmail)) {
        if (this.isAccountManagerContextForWaiver(waiver)) {
          this.emailErrorByWaiver = { ...this.emailErrorByWaiver, [key]: true };
        } else {
          this.managerErrorByWaiver = { ...this.managerErrorByWaiver, [key]: true };
        }
        return;
      }
      if (this.isSignatoryAlreadyAssigned(waiver, { email: signatoryEmail })) {
        this.$emit("alertNotValidate", this.$t("waivers.signatory_already_assigned"));
        return;
      }
      const payload = {
        membershipWaiverTemplateId: Number(waiver.membershipWaiverTemplateId || waiver.fkWaiverId || 0),
        riderUserId: Number(waiver.racerUserId || waiver.userId || 0),
        signatoryEmail
      };
      if (!payload.membershipWaiverTemplateId || !payload.riderUserId) {
        this.$emit("alertNotValidate", this.$t("registerEvent.membership_waiver_generation_failed"));
        return;
      }
      this.loadingByWaiver = { ...this.loadingByWaiver, [key]: true };
      try {
        const response = await MembershipWaiverService.addAnotherSignatory(payload);
        if (response?.data?.success === 1) {
          this.$emit("showAlert", { msg: this.$t("registerEvent.send_email_successfully"), color: "success" });
          this.$emit("refreshMembershipWaiverStatus");
          return;
        }
        const errKey = (response?.data?.message || "").toString().trim();
        if (errKey === "user_not_found_collect_info") {
          this.openNoAccountRegistration(waiver, "additional", signatoryEmail);
          return;
        }
        this.$emit("alertNotValidate", this.translateMembershipWaiverError(waiver, errKey));
      } catch (error) {
        this.$emit("alertNotValidate", this.$t("registerEvent.membership_waiver_assignment_failed"));
      } finally {
        this.loadingByWaiver = { ...this.loadingByWaiver, [key]: false };
      }
    },
    async findOrCreateSignatory(waiver) {
      const key = this.getWaiverKey(waiver);
      this.emailErrorByWaiver = { ...this.emailErrorByWaiver, [key]: false };
      this.managerErrorByWaiver = { ...this.managerErrorByWaiver, [key]: false };

      if (waiver.isMembershipWaiver && this.isMinorWaiver(waiver) && !this.isAccountManagerContextForWaiver(waiver)) {
        const selectedManagerEmail = (this.selectedManagerByWaiver[key] || "").trim();
        if (!this.isValidEmail(selectedManagerEmail)) {
          this.managerErrorByWaiver = { ...this.managerErrorByWaiver, [key]: true };
          return;
        }
        this.loadingByWaiver = { ...this.loadingByWaiver, [key]: true };
        try {
          await this.executeMembershipGenerate(waiver, {
            legalGuardianAnswer: "no",
            signatoryEmail: selectedManagerEmail
          });
          return;
        } catch (error) {
          const keyFromError = (error?.message || "").toString().trim();
          if (keyFromError === "user_not_found_collect_info") {
            this.openNoAccountRegistration(waiver, "primary", selectedManagerEmail);
            return;
          }
          this.$emit("alertNotValidate", this.translateMembershipWaiverError(waiver, keyFromError));
        } finally {
          this.loadingByWaiver = { ...this.loadingByWaiver, [key]: false };
        }
        return;
      }

      const email = (this.emailByWaiver[key] || "").trim();

      if (!this.isValidEmail(email)) {
        this.emailErrorByWaiver = { ...this.emailErrorByWaiver, [key]: true };
        return;
      }

      this.loadingByWaiver = { ...this.loadingByWaiver, [key]: true };
      try {
        if (waiver.isMembershipWaiver) {
          const legalGuardianAnswer = this.isMinorWaiver(waiver) ? "no" : null;
          await this.executeMembershipGenerate(waiver, { legalGuardianAnswer, signatoryEmail: email });
          return;
        }
        let signatory = await this.lookupSignatoryByEmail(email);
        if (!signatory) {
          signatory = await this.createSpectatorFromEmail(email);
        }
        if (!signatory || !signatory.id) {
          throw new Error("assignment_failed");
        }

        await this.assignSignatoryToWaiver(waiver, signatory.id);

        this.assignedByWaiver = {
          ...this.assignedByWaiver,
          [key]: { name: signatory.name || email }
        };
        const { firstName, lastName } = this.getNameParts(signatory.name || "");
        waiver.signUserId = signatory.id;
        waiver.signatoryEmail = email;
        waiver.signatoryName = firstName || waiver.signatoryName || "";
        waiver.signatoryLastname = lastName || waiver.signatoryLastname || "";

        const waiverId = waiver.waiverInstanceId || waiver.id || waiver.eventWaiverId || waiver.membershipWaiverTemplateId;
        try {
          const resp = await EventWaiverService.sendWaiver({ email, waiverId });
          if (!resp?.data?.success) {
            this.$emit("alertNotValidate", this.$t("registerEvent.membership_waiver_email_send_failed"));
          } else {
            this.$emit("showAlert", { msg: this.$t("registerEvent.send_email_successfully"), color: "success" });
          }
        } catch (error) {
          this.$emit("alertNotValidate", this.$t("registerEvent.membership_waiver_email_send_failed"));
        } finally {
          this.$emit("refreshMembershipWaiverStatus");
        }
      } catch (error) {
        const keyFromError = (error?.message || "").toString().trim();
        if (keyFromError === "user_not_found_collect_info") {
          this.openNoAccountRegistration(waiver, "primary", email);
        } else {
          this.$emit("alertNotValidate", this.translateMembershipWaiverError(waiver, keyFromError));
        }
      } finally {
        this.loadingByWaiver = { ...this.loadingByWaiver, [key]: false };
      }
    },
    async lookupSignatoryByEmail(email) {
      const finders = ["emailAddressSpectator", "emailAddress"];
      for (const field of finders) {
        try {
          const resp = await RelationshipService.relationshipFinder(field, email);
          const rows = Array.isArray(resp?.data?.message) ? resp.data.message : [];
          if (rows.length > 0) {
            const candidate = rows.find((row) =>
              (row.emailAddress || row.email || "").toLowerCase() === email.toLowerCase()
            );
            if (!candidate) {
              continue;
            }
            const first = candidate.firstName || candidate.fName || "";
            const last = candidate.lastName || candidate.lName || "";
            return {
              id: candidate.id,
              name: `${first} ${last}`.trim() || email
            };
          }
        } catch (error) {
          // Continue with next finder.
        }
      }
      return null;
    },
    async createSpectatorFromEmail(email) {
      const localPart = (email.split("@")[0] || "").trim();
      const normalized = localPart.length > 0 ? localPart : "signatory";
      const first = normalized.slice(0, 32);
      const last = "account";
      const payload = {
        userId: this.userStore?.id,
        birthday: "1900-01-01",
        firstname: first,
        gender: "male",
        lastname: last,
        address: "",
        city: "",
        country: "",
        state: "",
        zip: "",
        ec_email: email,
        ec_firstname: first,
        ec_lastname: last,
        ec_phone: "",
        ec_cell: "",
        email,
        password: ""
      };
      const resp = await AuthService.registrationParticipant(payload, "Spectator");
      if (resp?.data?.success !== 1) {
        return null;
      }
      const message = resp?.data?.message;
      if (typeof message === "number" || typeof message === "string") {
        return { id: Number(message), name: `${first} ${last}`.trim() };
      }
      if (message && typeof message === "object") {
        const nestedId = message.id || (message.original && message.original.id) || message.userId;
        if (nestedId) {
          const nestedName = `${message.firstName || first} ${message.lastName || last}`.trim();
          return { id: Number(nestedId), name: nestedName || `${first} ${last}`.trim() };
        }
      }
      return null;
    },
    async assignSignatoryToWaiver(waiver, signatoryUserId) {
      const waiverCandidates = [
        waiver.membershipWaiverTemplateId,
        waiver.fkWaiverId,
        waiver.waiverInstanceId,
        waiver.id,
        waiver.eventWaiverId
      ]
        .map((value) => Number(value))
        .filter((value, index, arr) => value > 0 && !Number.isNaN(value) && arr.indexOf(value) === index);
      if (waiverCandidates.length === 0) {
        throw new Error("assignment_failed");
      }
      const racerUserId = Number(waiver.racerUserId || waiver.userId);
      if (!racerUserId || Number.isNaN(racerUserId)) {
        throw new Error("assignment_failed");
      }

      let lastResponse = null;
      for (const waiverId of waiverCandidates) {
        const payload = {
          waiver: waiverId,
          parent: signatoryUserId,
          userId: racerUserId
        };
        const resp = await WaiverService.addSignatory(payload);
        lastResponse = resp;
        if (resp?.data?.success === 1 && resp?.data?.message !== "not_add_signatory") {
          return;
        }
      }
      throw new Error(lastResponse?.data?.message || "assignment_failed");
    },
    async signOrSendWaiver(waiver, manageLoading = true) {
      const key = this.getWaiverKey(waiver);
      if (manageLoading) {
        this.loadingByWaiver = { ...this.loadingByWaiver, [key]: true };
      }
      try {
        if (waiver.isMembershipWaiver) {
          await this.executeMembershipGenerate(waiver);
          return;
        }
        const racerUserId = waiver.racerUserId || waiver.userId;
        await this.generateWaiver(waiver.eventWaiverId, racerUserId, this.role);
      } catch (error) {
        this.$emit("alertNotValidate", this.$t("common.error_general"));
      } finally {
        if (manageLoading) {
          this.loadingByWaiver = { ...this.loadingByWaiver, [key]: false };
        }
      }
    },
    async generateWaiver(eventWaiverId, userId, role) {
      try {
        const response = await EventWaiverService.generateWaiverByEventWaiverId({
          eventWaiverId,
          userId,
          role
        });

        if (response.data.success) {
          this.handleWaiverSuccess(response.data.message);
        } else {
          this.handleWaiverError(response.data.message);
        }
      } catch (error) {
        this.$emit("alertNotValidate", this.$t("common.error_general"));
      }
    },
    handleWaiverSuccess(message) {
      if (message.id) {
        this.$emit("addMembershipWaiver", message);
        this.$emit("refreshMembershipWaiverStatus");
      } else {
        this.bSendWaiver = true;
        this.$emit("showAlert", { msg: this.$t("registerEvent.send_email_successfully"), color: "success" });
        this.$emit("refreshMembershipWaiverStatus");
      }
    },
    handleWaiverError(message) {
      let errorMessage = this.$t("common.error_general");
      if (typeof message === "string") {
        errorMessage = this.$te("registerEvent." + message)
          ? this.$t("registerEvent." + message)
          : this.$t("common.error_general");
      } else if (Array.isArray(message)) {
        errorMessage = this.$te("registerEvent." + message[0])
          ? this.$t("registerEvent." + message[0])
          : this.$t("common.error_general");
      }
      this.$emit("alertNotValidate", errorMessage);
    }
  },
  watch: {
    waiverDialog(value) {
      if (!value) {
        this.$emit("refreshMembershipWaiverStatus");
        this.guardianAnswer = {};
        this.emailByWaiver = {};
        this.emailErrorByWaiver = {};
        this.loadingByWaiver = {};
        this.assignedByWaiver = {};
        this.managerOptionsByWaiver = {};
        this.relatedAdultOptionsByWaiver = {};
        this.selectedManagerByWaiver = {};
        this.managerErrorByWaiver = {};
        this.showNoAccountFormByWaiver = {};
        this.noAccountActionByWaiver = {};
        this.soleGuardianWaivers = {};
        return;
      }
      const waivers = this.membershipWaiversUser || [];
      waivers.forEach((waiver) => {
        if (waiver?.isMembershipWaiver && this.isMinorWaiver(waiver)) {
          this.loadAccountManagerOptionsForWaiver(waiver);
          if (this.isAccountManagerContextForWaiver(waiver) && this.canAssignAdditionalSignatory(waiver)) {
            this.loadRelatedAdultOptionsForWaiver(waiver);
          }
        }
      });
    },
    selectedParticipant() {
      this.showNoAccountFormByWaiver = {};
      this.noAccountActionByWaiver = {};
      const waivers = this.membershipWaiversUser || [];
      waivers.forEach((waiver) => {
        if (waiver?.isMembershipWaiver && this.isMinorWaiver(waiver)) {
          this.loadAccountManagerOptionsForWaiver(waiver);
          if (this.isAccountManagerContextForWaiver(waiver) && this.canAssignAdditionalSignatory(waiver)) {
            this.loadRelatedAdultOptionsForWaiver(waiver);
          }
        }
      });
    },
    waiversUser() {
      this.guardianAnswer = {};
      this.emailByWaiver = {};
      this.emailErrorByWaiver = {};
      this.loadingByWaiver = {};
      this.assignedByWaiver = {};
      this.managerOptionsByWaiver = {};
      this.relatedAdultOptionsByWaiver = {};
      this.selectedManagerByWaiver = {};
      this.managerErrorByWaiver = {};
      this.showNoAccountFormByWaiver = {};
      this.noAccountActionByWaiver = {};
      const waivers = this.membershipWaiversUser || [];
      waivers.forEach((waiver) => {
        if (waiver?.isMembershipWaiver && this.isMinorWaiver(waiver)) {
          this.loadAccountManagerOptionsForWaiver(waiver);
          if (this.isAccountManagerContextForWaiver(waiver) && this.canAssignAdditionalSignatory(waiver)) {
            this.loadRelatedAdultOptionsForWaiver(waiver);
          }
        }
      });
    }
  }
};
</script>

<style scoped>
.membership-waiver-card-title {
  padding-bottom: 4px;
}

.membership-waiver-card-text {
  padding-top: 4px;
}

.sole-guardian-btn {
  min-height: 36px;
  height: auto !important;
  white-space: normal;
}
</style>
